import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EmailType, EmailTypeEnum } from "../../Enums/EmailEnum";
import DropDownList from "../../components/Atomos/DropDown/Dropdown";
import GenericFileInputButton from "../../components/Atomos/FileInput/GenericFileInputButton";
import Input from "../../components/Atomos/Inputs/Input";
import PencilEdit from "../../components/Atomos/Icons/PencilEdit";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import { IEmailCategory } from "../../interfaces/models/IEmailCategory";
import { IEmailTemplate } from "../../interfaces/models/IEmailTemplate";
import { getEmailCategories } from "../../Services/EmailCategory";
import { getEmailTemplateById, updateEmailTemplate } from "../../Services/EmailTemplate";
import { getEventById, getEventLookupOptions } from "../../Services/Event";
import { toastPromise } from "../../utils/toast";
import { uploadImage } from "../../utils/UploadImage";
import { EmailTemplate } from "./types";
import CustomFromEmailFields from "./CustomFromEmailFields";

interface EnvelopeInformationCardProps {
  selectedTemplate: EmailTemplate | null;
  onTemplateUpdated?: (template: IEmailTemplate) => void;
  onEditingChange?: (isEditing: boolean) => void;
}

type EnvelopeFormState = {
  sender: string;
  fromEmail: string;
  subject: string;
  title: string;
  emailEventId: string;
  category: string;
  emailType: string;
  attachment: string;
};

const EMPTY_VALUE = "-";
const ATTACHMENT_ACCEPT = ".gif,.png,.jpg,.jpeg,.pdf,.xlsx,.docx";
const MAX_FILE_SIZE_MB = 25;
const ENVELOPE_ATTACHMENT_PATH = "email/envelopes/";

const EMAIL_TYPE_OPTIONS = Object.entries(EmailTypeEnum).map(([value, label]) => ({
  id: value,
  name: label,
}));

const EMPTY_FORM: EnvelopeFormState = {
  sender: "",
  fromEmail: "",
  subject: "",
  title: "",
  emailEventId: "",
  category: "",
  emailType: "",
  attachment: "",
};

const getAttachmentName = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  const baseName = trimmed.startsWith("http")
    ? trimmed.split("/").filter(Boolean).pop() || trimmed
    : trimmed;

  if (/^\d+/.test(baseName)) {
    return baseName.replace(/^\d+/, "").trim() || baseName;
  }

  return baseName;
};

const parseAttachments = (attachment?: string): string[] => {
  const source = attachment?.trim() || "";
  if (!source) {
    return [];
  }

  if (source.startsWith("[")) {
    try {
      const parsed = JSON.parse(source);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => {
            if (typeof item === "string") {
              return getAttachmentName(item);
            }
            if (item && typeof item === "object") {
              const attachmentLike = item as { name?: string; url?: string };
              return getAttachmentName(attachmentLike.name || attachmentLike.url || "");
            }
            return "";
          })
          .filter(Boolean);
      }
    } catch {
      // Fallback to plain string parsing
    }
  }

  return source
    .split(/[\n,;]/)
    .map((item) => getAttachmentName(item))
    .filter(Boolean);
};

const resolveTemplateTitle = (template: EmailTemplate | null): string => {
  const title = template?.title?.trim();
  if (title) {
    return title;
  }
  const name = template?.name?.trim() || "";
  const match = name.match(/^\d+\s+-\s+(.*)$/);
  return (match?.[1] || name).trim();
};

const buildFormFromTemplate = (template: EmailTemplate | null): EnvelopeFormState => ({
  sender: template?.sender?.trim() || "",
  fromEmail: template?.fromEmail?.trim() || "",
  subject: template?.subject?.trim() || "",
  title: resolveTemplateTitle(template),
  emailEventId: template?.emailEventId?.trim() || "",
  category: template?.category?.trim() || "",
  emailType: template?.emailType?.trim() || "",
  attachment: template?.attachment?.trim() || "",
});

function ReadOnlyField({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-lg font-semibold leading-none text-gray-font">
        {label}
      </span>
      <span className="text-sm text-gray-600">{value}</span>
    </div>
  );
}

export default function EnvelopeInformationCard({
  selectedTemplate,
  onTemplateUpdated,
  onEditingChange,
}: EnvelopeInformationCardProps): React.ReactElement {
  const [eventTitle, setEventTitle] = useState(EMPTY_VALUE);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<EnvelopeFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [categories, setCategories] = useState<IEmailCategory[]>([]);
  const [eventOptions, setEventOptions] = useState<{ id: string; name: string }[]>([]);
  const lastSyncedTemplateIdRef = useRef<string | undefined>(undefined);
  const isFirstTemplateSyncRef = useRef(true);

  useEffect(() => {
    const eventId = selectedTemplate?.emailEventId?.trim() || "";
    if (!eventId) {
      setEventTitle(EMPTY_VALUE);
      return;
    }

    let cancelled = false;
    getEventById(eventId).then((event) => {
      if (cancelled) {
        return;
      }
      const resolvedTitle = event?.title?.trim();
      setEventTitle(resolvedTitle || eventId);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedTemplate?.emailEventId]);

  useEffect(() => {
    const currentId = selectedTemplate?.id;
    const shouldReset =
      isFirstTemplateSyncRef.current || lastSyncedTemplateIdRef.current !== currentId;
    isFirstTemplateSyncRef.current = false;
    if (!shouldReset) {
      return;
    }

    lastSyncedTemplateIdRef.current = currentId;
    setIsEditing(false);
    setSaving(false);
    setErrors({});
    setAttachmentFile(null);
    setForm(buildFormFromTemplate(selectedTemplate));
    onEditingChange?.(false);
  }, [selectedTemplate, onEditingChange]);

  useEffect(() => {
    return () => {
      onEditingChange?.(false);
    };
  }, [onEditingChange]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    let cancelled = false;
    getEmailCategories().then((data) => {
      if (!cancelled) {
        setCategories(data ?? []);
      }
    });
    getEventLookupOptions().then((data) => {
      if (cancelled) {
        return;
      }
      setEventOptions(
        (data ?? []).map((event) => ({
          id: event.id ?? "",
          name: event.title?.trim() ? event.title : (event.id ?? "Untitled"),
        })),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [isEditing]);

  const emailTypeLabel = useMemo(() => {
    if (!selectedTemplate?.emailType) {
      return EMPTY_VALUE;
    }
    return (
      EmailTypeEnum[selectedTemplate.emailType as keyof typeof EmailTypeEnum] ||
      selectedTemplate.emailType
    );
  }, [selectedTemplate?.emailType]);

  const attachments = useMemo(
    () => parseAttachments(selectedTemplate?.attachment),
    [selectedTemplate?.attachment],
  );

  const from = selectedTemplate?.sender?.trim() || EMPTY_VALUE;
  const fromEmail = selectedTemplate?.fromEmail?.trim() || EMPTY_VALUE;
  const subject = selectedTemplate?.subject?.trim() || EMPTY_VALUE;
  const title = resolveTemplateTitle(selectedTemplate) || EMPTY_VALUE;
  const event = eventTitle;
  const category = selectedTemplate?.category?.trim() || EMPTY_VALUE;
  const canEdit = Boolean(selectedTemplate?.id);

  const updateForm = useCallback(
    (field: keyof EnvelopeFormState, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
    },
    [],
  );

  const startEditing = useCallback(() => {
    if (!selectedTemplate?.id) {
      return;
    }
    setForm(buildFormFromTemplate(selectedTemplate));
    setAttachmentFile(null);
    setErrors({});
    setIsEditing(true);
    onEditingChange?.(true);
  }, [onEditingChange, selectedTemplate]);

  const cancelEditing = useCallback(() => {
    if (saving) {
      return;
    }
    setForm(buildFormFromTemplate(selectedTemplate));
    setAttachmentFile(null);
    setErrors({});
    setIsEditing(false);
    onEditingChange?.(false);
  }, [onEditingChange, saving, selectedTemplate]);

  const validate = useCallback((): boolean => {
    const next: Record<string, string> = {};
    if (!form.category.trim()) {
      next.category = "Category is required.";
    }
    if (!form.emailType.trim()) {
      next.emailType = "Type is required.";
    }
    if (!form.title.trim()) {
      next.title = "Title is required.";
    }
    if (!form.subject.trim()) {
      next.subject = "Subject is required.";
    }
    if (!form.fromEmail.trim()) {
      next.fromEmail = "From Email is required.";
    }
    if (!form.sender.trim()) {
      next.sender = "From is required.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [form]);

  const handleSave = useCallback(async () => {
    if (saving || !selectedTemplate?.id || !validate()) {
      return;
    }

    setSaving(true);
    try {
      await toastPromise(
        (async () => {
          let attachmentUrl = form.attachment.trim();
          if (attachmentFile) {
            const uploaded = await uploadImage(
              attachmentFile,
              "envelope",
              ENVELOPE_ATTACHMENT_PATH,
            );
            if (!uploaded) {
              throw new Error("Unable to upload attachment.");
            }
            attachmentUrl = uploaded;
          }

          const current = await getEmailTemplateById(selectedTemplate.id);
          if (!current?.id) {
            throw new Error("Could not load envelope information.");
          }

          const updated = await updateEmailTemplate({
            ...current,
            title: form.title.trim(),
            category: form.category.trim(),
            subject: form.subject.trim(),
            sender: form.sender.trim(),
            fromEmail: form.fromEmail.trim(),
            emailType: (form.emailType as EmailType) || current.emailType,
            attachment: attachmentUrl,
            emailEventId: form.emailEventId.trim() || undefined,
          });

          onTemplateUpdated?.(updated?.id ? updated : {
            ...current,
            title: form.title.trim(),
            category: form.category.trim(),
            subject: form.subject.trim(),
            sender: form.sender.trim(),
            fromEmail: form.fromEmail.trim(),
            emailType: (form.emailType as EmailType) || current.emailType,
            attachment: attachmentUrl,
            emailEventId: form.emailEventId.trim() || undefined,
          });
        })(),
        {
          pending: "Saving envelope information...",
          success: "Envelope information updated.",
        },
      );
      setIsEditing(false);
      onEditingChange?.(false);
    } catch {
      // toastPromise already shows the error.
    } finally {
      setSaving(false);
    }
  }, [
    attachmentFile,
    form,
    onEditingChange,
    onTemplateUpdated,
    saving,
    selectedTemplate?.id,
    validate,
  ]);

  const attachmentUrl = form.attachment.trim();
  const attachmentTitle = attachmentUrl ? getAttachmentName(attachmentUrl) : "";

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-6 lg:px-8 py-5 border-b border-gray-100">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-[2rem] font-decimal font-medium leading-none text-Default">
            Envelope Information
          </h3>
          {!isEditing && canEdit ? (
            <button
              type="button"
              aria-label="Edit envelope information"
              onClick={startEditing}
              className="inline-flex items-center justify-center text-gray-font hover:text-accent-Default transition-colors"
            >
              <PencilEdit className="w-5 h-5" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="px-6 lg:px-8 py-6">
        {isEditing ? (
          <div className="flex flex-col gap-6">
            <CustomFromEmailFields
              layout="envelope"
              fromEmail={form.fromEmail}
              sender={form.sender}
              errors={{ fromEmail: errors.fromEmail, sender: errors.sender }}
              onChange={({ fromEmail, sender }) => {
                setForm((prev) => ({ ...prev, fromEmail, sender }));
              }}
              onClearErrors={(fields) => {
                setErrors((prev) => {
                  const next = { ...prev };
                  (fields || ["fromEmail", "sender"]).forEach((field) => {
                    next[field] = "";
                  });
                  return next;
                });
              }}
              trailingFields={
                <div className="col-span-12 md:col-span-4">
                  <Input
                    title="Subject"
                    name="envelope-subject"
                    value={form.subject}
                    onChange={(event) => updateForm("subject", event.target.value)}
                    error={!!errors.subject}
                    errorMsj={errors.subject}
                    required
                  />
                </div>
              }
            />

            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-8">
                <Input
                  title="Title"
                  name="envelope-title"
                  value={form.title}
                  onChange={(event) => updateForm("title", event.target.value)}
                  error={!!errors.title}
                  errorMsj={errors.title}
                  required
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <DropDownList
                  title="Event"
                  name="envelope-emailEventId"
                  data={eventOptions}
                  selectedObjst={
                    eventOptions.find((option) => option.id === form.emailEventId)?.name
                    ?? undefined
                  }
                  seter={(val: string | null) => {
                    const option = eventOptions.find((item) => item.name === val);
                    updateForm("emailEventId", option?.id ?? "");
                  }}
                  error={!!errors.emailEventId}
                  errormsj={errors.emailEventId}
                  placeholder="Select event"
                  internalSearch
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-4">
                <DropDownList
                  title="Category"
                  name="envelope-category"
                  data={categories.map((item) => ({
                    id: item.id ?? item.name,
                    name: item.name,
                  }))}
                  selectedObjst={form.category || undefined}
                  seter={(val: string | { id: string; name: string } | null) =>
                    updateForm("category", typeof val === "string" ? val : val?.name ?? "")
                  }
                  required
                  error={!!errors.category}
                  errormsj={errors.category}
                  placeholder="Select category"
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <DropDownList
                  title="Type"
                  name="envelope-emailType"
                  data={EMAIL_TYPE_OPTIONS}
                  selectedObjst={
                    EMAIL_TYPE_OPTIONS.find((option) => option.id === form.emailType)?.name
                    ?? undefined
                  }
                  seter={(val: string | null) => {
                    const option = EMAIL_TYPE_OPTIONS.find((item) => item.name === val);
                    updateForm("emailType", option?.id ?? "");
                  }}
                  required
                  error={!!errors.emailType}
                  errormsj={errors.emailType}
                  placeholder="Select type"
                />
              </div>
            </div>

            <div className="border-t border-gray-200" />

            <div className="flex flex-col gap-3">
              <GenericFileInputButton
                title="Attachments"
                accept={ATTACHMENT_ACCEPT}
                maxSizeMb={MAX_FILE_SIZE_MB}
                onFileSelected={(file) => {
                  setAttachmentFile(file);
                  updateForm("attachment", file.name);
                }}
                onFileRemoved={() => {
                  setAttachmentFile(null);
                  updateForm("attachment", "");
                }}
                hint={`Allowed: .gif, .png, .jpg, .jpeg, .pdf, .xlsx, .docx. Max ${MAX_FILE_SIZE_MB} MB.`}
                variant="accent"
              />
              {!attachmentFile && attachmentTitle ? (
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span>
                    Current attachment:{" "}
                    {attachmentUrl.startsWith("http") ? (
                      <a
                        href={attachmentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-Default underline hover:no-underline"
                      >
                        {attachmentTitle}
                      </a>
                    ) : (
                      attachmentTitle
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => updateForm("attachment", "")}
                    className="text-sm text-[#474F56] underline hover:no-underline"
                  >
                    Remove
                  </button>
                </div>
              ) : null}
            </div>

            <div className="flex justify-end gap-4">
              <LegacyButton
                text="CANCEL"
                style="outlined"
                color="accent"
                content="text"
                size="small"
                onClick={cancelEditing}
                disabled={saving}
              />
              <LegacyButton
                text={saving ? "SAVING..." : "SAVE"}
                style="filled"
                color="accent"
                content="text"
                size="small"
                onClick={handleSave}
                disabled={saving}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-4">
                <ReadOnlyField label="From" value={from} />
              </div>
              <div className="col-span-12 md:col-span-4">
                <ReadOnlyField label="From Email" value={fromEmail} />
              </div>
              <div className="col-span-12 md:col-span-4">
                <ReadOnlyField label="Subject" value={subject} />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6 mt-6">
              <div className="col-span-12 md:col-span-8">
                <ReadOnlyField label="Title" value={title} />
              </div>
              <div className="col-span-12 md:col-span-4">
                <ReadOnlyField label="Event" value={event} />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6 mt-6">
              <div className="col-span-12 md:col-span-4">
                <ReadOnlyField label="Category" value={category} />
              </div>
              <div className="col-span-12 md:col-span-4">
                <ReadOnlyField label="Type" value={emailTypeLabel} />
              </div>
              <div className="col-span-12 md:col-span-4" />
            </div>

            <div className="border-t border-gray-200 my-6" />

            <div className="flex items-center gap-4">
              <span className="text-lg font-semibold leading-none text-gray-font">
                Attachments
              </span>
              <div className="flex flex-wrap items-center gap-3">
                {attachments.length > 0 ? (
                  attachments.map((attachment, index) => (
                    <div
                      key={`${attachment}-${index}`}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded bg-white"
                    >
                      <span className="text-sm text-gray-600">{attachment}</span>
                    </div>
                  ))
                ) : (
                  <span className="text-sm text-gray-600">No attachments</span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
